import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getAllSets } from '../services/pokemonTcgApi';
import { PokemonSet } from '../types';
import { slugify } from '../utils/slugify';
import { ArrowLeft, Calendar, DollarSign } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';
import { useCurrencyStore } from '../lib/currencyStore';
import { formatPrice } from '../utils/formatPrice';
import { supabase } from '../lib/supabase';
import LoadingHeader from '../components/LoadingHeader';

interface SetPriceStats {
  avgPrice: number;
  minPrice: number;
  maxPrice: number;
}

interface SetWithPrices extends PokemonSet {
  priceStats?: SetPriceStats;
}

export default function SeriesPage() {
  const { series } = useParams();
  const [sets, setSets] = useState<SetWithPrices[]>([]);
  const [loading, setLoading] = useState(true);
  const { currency } = useCurrencyStore();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSets = async () => {
      try {
        const allSets = await getAllSets();
        // Filter sets by series
        const seriesSets = allSets.filter(set => 
          slugify(set.series) === series
        );

        // Fetch price stats for each set
        const setsWithPrices = await Promise.all(seriesSets.map(async (set) => {
          try {
            const { data, error } = await supabase
              .from('pokemontcg_io')
              .select('market_price_trend_usd')
              .eq('set_id', set.id)
              .not('market_price_trend_usd', 'is', null);

            if (error) throw error;

            if (data && data.length > 0) {
              const prices = data.map(card => card.market_price_trend_usd).filter(Boolean);
              const priceStats = {
                avgPrice: prices.reduce((a, b) => a + b, 0) / prices.length,
                minPrice: Math.min(...prices),
                maxPrice: Math.max(...prices)
              };
              return { ...set, priceStats };
            }
            return set;
          } catch (error) {
            console.error(`Error fetching prices for set ${set.id}:`, error);
            return set;
          }
        }));

        setSets(setsWithPrices);
      } catch (error) {
        console.error('Error fetching sets:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSets();
  }, [series]);

  const formatReleaseDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="bg-white/5 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="mb-4">
              <Breadcrumbs />
            </div>
            <LoadingHeader />
          </div>
        </div>
      </div>
    );
  }

  if (sets.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white">
        <h1 className="text-2xl font-bold mb-4">Series Not Found</h1>
        <p className="text-white/60 mb-4">
          We couldn't find the series you're looking for.
        </p>
        <Link to="/" className="text-white/60 hover:text-white flex items-center gap-2">
          <ArrowLeft size={20} />
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
            >
              <ArrowLeft size={20} />
              Back
            </Link>
            <h1 className="text-2xl font-bold">{sets[0]?.series}</h1>
            <span className="text-white/60">{sets.length} sets</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {sets.map(set => (
            <div
              key={set.id}
              className="bg-white/5 border border-white/10 rounded-lg overflow-hidden group"
            >
              <Link
                to={`/${slugify(set.series)}/${slugify(set.name)}`}
                className="block"
              >
                {/* Set Logo */}
                <div className="relative aspect-video bg-gradient-to-br from-white/5 to-white/10 overflow-hidden">
                  <img
                    src={set.images?.logo}
                    alt={`${set.name} logo`}
                    className="absolute inset-0 w-full h-full object-contain p-6 transition-transform group-hover:scale-110"
                    loading="lazy"
                  />
                </div>

                {/* Set Info */}
                <div className="p-4">
                  <h2 className="text-lg font-semibold mb-2 group-hover:text-white/90">
                    {set.name}
                  </h2>
                  <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
                    <Calendar size={16} />
                    <span>{formatReleaseDate(set.releaseDate)}</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white/60">
                        {set.total} cards
                      </span>
                      {set.priceStats && (
                        <div className="flex items-center gap-1 text-white/80">
                          <DollarSign size={14} className="text-green-500" />
                          <span>Avg: {formatPrice(set.priceStats.avgPrice, currency)}</span>
                        </div>
                      )}
                    </div>
                    {set.priceStats && (
                      <div className="grid grid-cols-2 gap-2 text-xs text-white/60">
                        <div>
                          <span>Min: </span>
                          <span>{formatPrice(set.priceStats.minPrice, currency)}</span>
                        </div>
                        <div className="text-right">
                          <span>Max: </span>
                          <span>{formatPrice(set.priceStats.maxPrice, currency)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}